import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import './Modal.css';

const UserModal = ({ user, groups, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    groupIds: [],   // multi-group — replaces single groupId
    userType: 'user',
    languageCode: 'pt',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    console.log('[UserModal.jsx] mounted');
    return () => console.log('[UserModal.jsx] unmounted');
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        username:  user.username,
        password:  '',
        firstName: user.first_name || '',
        lastName:  user.last_name  || '',
        email:     user.email      || '',
        groupIds:      (user.groups || []).map(g => Number(g.group_id)),
        userType:      user.user_type  || 'user',
        languageCode:  user.language_code || 'pt',
        isActive:      user.is_active,
      });
    }
    setHasChanges(false);
  }, [user]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setHasChanges(true);
  };

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm(t('messages.confirm_close', 'You have unsaved changes. Are you sure you want to close?'))) {
        return;
      }
    }
    onClose(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.username || !formData.firstName || !formData.lastName) {
      setError(t('users.validation.required', 'Username, first name, and last name are required'));
      return;
    }
    
    if (!user && !formData.password) {
      setError(t('users.validation.password_required', 'Password is required for new users'));
      return;
    }
    
    if (formData.password && formData.password.length < 6) {
      setError(t('users.validation.password_length', 'Password must be at least 6 characters long'));
      return;
    }
    
    setLoading(true);
    
    try {
      const data = {
        ...formData,
        groupIds: formData.groupIds.map(Number),
      };
      
      if (user) {
        const updateData = { ...data };
        if (!updateData.password) {
          delete updateData.password;
        }
        delete updateData.username;
        
        await usersAPI.update(user.user_id, updateData);
      } else {
        await usersAPI.create(data);
      }
      
      setHasChanges(false);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || t('messages.error_save', 'Failed to save user'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{user ? t('users.edit', 'Edit User') : t('users.create_new', 'Create New User')}</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            type="button"
            title={t('close_esc', 'Close (ESC)')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {hasChanges && (
            <div className="form-warning">
              <span className="warning-icon">✏️</span>
              {t('messages.unsaved', 'You have unsaved changes')}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>{t('users.username', 'Username')} *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={!!user}
                required
                autoFocus={!user}
              />
            </div>

            {!user && (
              <div className="form-group">
                <label>{t('users.password', 'Password')} *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('users.password_min', 'Min. 6 characters')}
                  required={!user}
                />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('users.firstname', 'First Name')} *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('users.lastname', 'Last Name')} *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('users.email', 'Email')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>{t('users.groups', 'Grupos')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
                {groups.map(group => {
                  const gid = Number(group.group_id);
                  const checked = formData.groupIds.includes(gid);
                  return (
                    <label key={group.group_id}
                      style={{ display: 'flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 16,
                        border: `1.5px solid ${checked ? 'var(--primary)' : 'var(--gray-200)'}`,
                        background: checked ? '#f0fdf9' : 'white',
                        cursor: 'pointer', fontSize: 13, userSelect: 'none',
                        color: checked ? 'var(--primary)' : 'var(--gray-700)',
                        fontWeight: checked ? 600 : 400,
                      }}>
                      <input type="checkbox"
                        checked={checked}
                        style={{ display: 'none' }}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            groupIds: checked
                              ? prev.groupIds.filter(id => id !== gid)
                              : [...prev.groupIds, gid],
                          }));
                          setHasChanges(true);
                        }}/>
                      {group.group_name}
                    </label>
                  );
                })}
                {groups.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                    Sem grupos disponíveis
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>{t('users.type', 'User Type')}</label>
              <select
                name="userType"
                value={formData.userType}
                onChange={handleChange}
              >
                <option value="user">{t('users.type_user', 'User')}</option>
                <option value="admin">{t('users.type_admin', 'Admin')}</option>
                <option value="manager">{t('users.type_manager', 'Manager')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('users.language', 'Idioma')}</label>
              <select
                name="languageCode"
                value={formData.languageCode}
                onChange={handleChange}
              >
                <option value="pt">🇵🇹 Português</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
          </div>

          <div className="form-group-checkbox">
            <label>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <span>{t('users.active', 'Active User')}</span>
            </label>
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              {t('cancel', 'Cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading 
                ? t('saving', 'Saving...') 
                : (user ? t('users.update', 'Update User') : t('users.create', 'Create User'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;